import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

/**
 * VideoOuterWrapper for the outer container of the video.
 *
 * Two sizes. Full fills a whole column of QuoteGrid — what the landing page
 * used before it had anything to say. Compact is a tile beside a headline,
 * which is all the video needs to be now that the panel around it carries the
 * message.
 */
const VideoOuterWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19);

  ${({ $compact }) =>
    $compact
      ? `
    /* Fills its column rather than declaring a width of its own. The tile lives
       in StoryPanel's Head grid, whose right track steps down twice on narrow
       screens (212px -> 132px -> 116px), and a fixed width only tracked the
       first step: below 560px the video was 16px wider than its 116px column,
       overflowed it, and hung off the right edge of the page. Sizing from the
       column means the two can no longer disagree. */
    width: 100%;
    max-width: 212px;
    aspect-ratio: 212 / 134;
    flex: 0 0 auto;
    margin: 0;
  `
      : `
    height: calc(100% - 80px);
    width: auto;
    margin: 0 auto;
    margin-left: 10px;
    margin-top: 70px;

    @media screen and (max-width: 900px) {
      display: block;
      width: 95%;
      height: auto;
      margin: 0 auto;
      margin-top: 70px;
      max-height: 320px;
      max-width: 380px;
    }
  `}
`;

// VideoStyled for styling the actual video element
const VideoStyled = styled.video.attrs({
  playsInline: true,
})`
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensures the video covers the container while maintaining aspect ratio */
  object-position: center; /* Centers the video content in the middle */
  flex-shrink: 0;
`;

const ModelVideo = ({ videoSrc, playbackSpeed = 1.0, compact = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  return (
    <VideoOuterWrapper $compact={compact}>
      <VideoStyled
        ref={videoRef}
        src={videoSrc}
        autoPlay
        loop
        muted
        preload="auto"
      />
    </VideoOuterWrapper>
  );
};

export default ModelVideo;
